import _ from 'lodash';
import KeyboardNavigation from '../grid/keyboardNavigation';
import app from '$$app';
import { getMasterLinksByTaskId } from '../helpers/ganttHelpers';
import rights from "../../../components/rights";

class InlineEditors {
  #instance = null;

  newTaskType = null;

  lineHeight = null;

  #preventEditing = false;

  init(instance) {
    this.#instance = instance;
    this.#preventEditing = false;
    this.newTaskType = this.#instance.config.types.task;
    this.lineHeight = 0;
    this.#overrideDefaultMapping();
  }

  disableEditing() {
    this.#preventEditing = true;
  }

  enableEditing() {
    setTimeout(() => {
      this.#preventEditing = false;
    });
  }

  #overrideDefaultMapping() {
    const defaultMapping = this.getMapping();
    let currentNode;

    defaultMapping.onShow = (inlineEditors, node) => {
      node.onkeydown = e => {
        const { id: taskId } = this.getState();
        const isCreateTaskButton = this.#instance.getTask(taskId).type === this.#instance.config.types.button;

        if (!isCreateTaskButton && ['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(e.key)) {
          e.stopPropagation();
        }
      };
      currentNode = node;
    };

    defaultMapping.onHide = () => {
      if (currentNode) {
        currentNode.onkeydown = null;
        currentNode = null;
      }
    };
  }

  onBeforeEditStart(id, columnName) {
    const column = this.#instance.getGridColumns().find(item => item.name === columnName);
    const task = this.#instance.getTask(id);

    if (task.type === this.#instance.config.types.button) {
      return !this.#preventEditing;
    }

    if (+id < 0) {
      return false;
    }

    if (!this.hasEditor(column, task) || !this.#instance.hasAccessToTask(task) || !this.#instance.hasRightToEditField(task.gantt_id, column.data_type ? column.data_type : column.name)) {
      return false;
    }

    if (column.isUserCustomColumn && !this.#instance.canEditColumn(task, column.name)) {
      return false;
    }

    if (column.isUserCustomColumn && column.data_type === 'custom_checkbox') {
      this.#instance.ganttDataApi.changeUserCustomValue(task, column.name, null);

      userExtAnalytics.log('task_grid_action', { action: 'custom_field', type: 'checkbox' });

      return false;
    }

    return !this.#preventEditing;
  }

  onEditStart(id, columnName) {
    const column = this.#instance.getGridColumns().find(item => item.name === columnName);
    const task = this.#instance.getTask(id);
    const mode = columnName === 'text' && _.isString(id) && id.includes('_buttons') ? 'create' : 'edit';

    app.trigger('gantt:inlineEditor:opened', mode, +id, +task.gantt_id, +task.parent, columnName, !!column.isCustomColumn, !!column.isUserCustomColumn);

    KeyboardNavigation.setCurrentEditingRowAndColumnIndices(id, columnName);

    if (task.type === this.#instance.config.types.button) {
      const row = document.querySelector(`.gantt_row[${this.#instance.config.task_attribute}="${id}"]`);

      row && row.classList.add('active');
      row && this.#addVerticalLine(row, task);

      return;
    }

    this.#instance.selectTask(id);
  }

  onEditEnd(id, columnName) {
    app.trigger('gantt:inlineEditor:closed');

    const task = this.#instance.getTask(id);

    if (task.type === this.#instance.config.types.button) {
      const row = document.querySelector(`.gantt_row[${this.#instance.config.task_attribute}="${id}"]`);

      row && row.classList.remove('active');
      this.lineHeight = 0;

      return;
    }
    this.#instance.unselectTask();
  }

  #addVerticalLine(row, task) {
    const line = row.querySelector('.line');
    const lineHeight = this.#getLineHeight(this.#instance.getChildren(task.parent));

    if (lineHeight) {
      line.style.height = `${lineHeight}px`;
      this.lineHeight = lineHeight;
    }
  }

  #getLineHeight(allChildren) {
    const rows = [];
    const getChildren = children => {
      if (!children.length) {
        return;
      }

      _.each(children, child => {
        this.#instance.isTaskVisible(child) && rows.push(child);
        if (this.#instance.getTask(child).$open) {
          getChildren(this.#instance.getChildren(child));
        }
      });
    };

    getChildren(allChildren);

    if (this.#instance.config.baseline === 1) {
      return rows.length * (this.#instance.config.row_height + 1) - 10;
    }

    return rows.length * (this.#instance.config.row_height) - this.#instance.config.row_height / 2 + 5;
  }

  onClick(save = false) {
    const state = this.getState();

    if (state.editor && this.#instance.getTask(state.id).type === this.#instance.config.types.button && !state.editor.get_value()) {
      // close task creation editor
      this.hide();
    } else if (state.editor && save) {
      this.save();
    }
  }

  getState() {
    return this.#instance.ext.inlineEditors.getState();
  }

  startEdit(taskId, columName) {
    this.#instance.ext.inlineEditors.startEdit(taskId, columName);
  }

  save() {
    this.#instance.ext.inlineEditors.save();
  }

  hide() {
    this.#instance.ext.inlineEditors.hide();
  }

  isActive() {
    const { editor } = this.getState();

    return !!editor;
  }

  getMapping() {
    return this.#instance.ext.inlineEditors.getMapping();
  }

  setMapping(mapping) {
    return this.#instance.ext.inlineEditors.setMapping(mapping);
  }

  isChanged() {
    return this.#instance.ext.inlineEditors.isChanged();
  }

  hasEditor(column, task) {
    if(['total_price', 'actual_cost'].includes(column.name) && task.type === 'project') return false;

    if (!column) {
      return false;
    }

    if (this.#instance.config.multiview && task.$level === 0) return false;

    let isHasEditor;
    const isTotalEstimate = this.#instance.config.multiview ? task.$level === 1 : task.$level === 0;

    if (isTotalEstimate && column.name !== 'start_date') {
      return false;
    }

    isHasEditor = !!column.editor && (!column.task_types || !task.type || column.task_types.indexOf(task.type) !== -1);

    if (this.checkByLinkDependency(task, column.name)) {
      isHasEditor = false;
    }

    if (task.$level === 0 && column.name !== 'start_date') {
      isHasEditor = false;
    }

    const projectAutoBudget = this.#instance.ganttDataApi.getProjectAutoBudget(task.gantt_id);
    const canEditBudget = ['total_price', 'actual_cost'].includes(column.name) && !projectAutoBudget && rights.project.hasCostRight(task.gantt_id);

    if (['total_price', 'actual_cost'].includes(column.name) && !canEditBudget) {
      isHasEditor = false;
    }

    return isHasEditor;
  }

  checkByLinkDependency(taskData, columnName) {
    if (columnName === 'create_date' || columnName === 'creator' || taskData.type === this.#instance.config.types.button) {
      return true;
    }

    if (!(columnName === 'start_date' || columnName === 'end_date') || !this.#instance.config.auto_scheduling.enabled) {
      return false;
    }

    if (columnName === 'start_date') {
      const children = this.#instance.getChildren(taskData.parent).filter(childID => this.#instance.getTask(childID).type !== this.#instance.config.types.button);
      const predecessors = this.#instance._getPredecessors(taskData, children.length > 1);

      if (predecessors.length === 1) {
        return true;
      }
    }

    const mastersData = getMasterLinksByTaskId(this.#instance, taskData.id);
    let shouldDisable = false;
    let alreadyBlockedByStart = false;

    mastersData.forEach(masterData => {
      switch (masterData.linkType) {
      case this.#instance.config.links.finish_to_start:
      case this.#instance.config.links.start_to_start:
        if (columnName === 'start_date') {
          shouldDisable = true;
        }
        if (columnName === 'end_date') {
          alreadyBlockedByStart = true;
          shouldDisable = false;
        }
        break;
      case this.#instance.config.links.finish_to_finish:
      case this.#instance.config.links.start_to_finish:
        if (columnName === 'end_date' && !alreadyBlockedByStart) {
          shouldDisable = true;
        }
        break;
      }
    });

    return shouldDisable;
  }
}

export default new InlineEditors();
